import { CompanyRole, companiesService } from "@/api/companies";
import { investorVettingService, type TVettingUserStatus } from "@/api/investorVetting";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog/Dialog";
import { TOnboardingCompletionResult, useOnboardingSession } from "@/hooks";
import {
  SKIP_UTTERANCE,
  findSimilarPriorAnswer,
  isSkipText,
  projectSessionProgress,
} from "@/lib/onboarding/sessionAdapter";
import type { TMessage, TMessageFile } from "@/types/chat";
import { getAnswerGuidanceFromRequirements, getAnswerTypeLabel } from "@/utils/answerTypeUtils";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowDown, ArrowRight, CheckCircle2, Loader2, MessageCircle, RefreshCw } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TOnboardingQuestionMetadata } from "@/api/onboarding";
import { FeedbackWidget } from "@/components/feedback/FeedbackWidget";
import OnboardingMessageInput from "@/components/onboarding/OnboardingMessageInput";
import OnboardingMessageList from "@/components/onboarding/OnboardingMessageList";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useStore } from "@/store";
import { getCreateCompanyErrorMessage } from "@/utils/errorUtils";

type TOnboardingQuestionsProps = {
  onCancel: () => void;
  onComplete: (payload: TOnboardingCompletionResult) => void;
  /** When true, inject a "generating summary" AI bubble into the chat stream */
  summaryGenerating?: boolean;
  /** Live generation status, shown inside the in-chat progress bubble. */
  summaryProgress?: { stage?: string | null; elapsedSeconds?: number | null };
};

type TPreOnboardingStep = "role_selection" | "company_name" | "creating_company";

// Transient bubbles that must always sit at the end of the stream, in this order.
const TAIL_IDS = ["ai-thinking", "ai-final-message", "ai-summary-generating"];

export function OnboardingQuestions({
  onCancel,
  onComplete,
  summaryGenerating = false,
  summaryProgress,
}: TOnboardingQuestionsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams({ strict: false });
  const routeCompanyId = params.company_id;

  // Pre-onboarding state
  const [preOnboardingStep, setPreOnboardingStep] = useState<TPreOnboardingStep>("role_selection");
  const [selectedRole, setSelectedRole] = useState<CompanyRole | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [preOnboardingError, setPreOnboardingError] = useState<string | null>(null);
  const [createdCompany, setCreatedCompany] = useState<{ id: string; name: string } | null>(null);
  const [isCheckingExistingCompany, setIsCheckingExistingCompany] = useState(true);
  const [hasNavigated, setHasNavigated] = useState(false);

  // Investor vetting gate (006/RD-1): investors are vetted by a human before
  // the Q&A starts. The WebSocket is never opened while vetting is pending.
  // 078: vetting can be disabled server-side; the status endpoint is the ONLY
  // source of that flag (no frontend config). While it is unknown we fail safe
  // to "enabled" so the gate can never be skipped against a vetting backend.
  const [vettingStatus, setVettingStatus] = useState<TVettingUserStatus | "unknown">("unknown");
  const [vettingEnabled, setVettingEnabled] = useState<boolean | "unknown">("unknown");
  const [synopsisValue, setSynopsisValue] = useState("");
  const [isSubmittingSynopsis, setIsSubmittingSynopsis] = useState(false);
  const [synopsisError, setSynopsisError] = useState<string | null>(null);

  const storeCompanies = useStore((state) => state.companies);
  const activeCompanyId = routeCompanyId ?? createdCompany?.id;
  const activeCompanyRole: CompanyRole | null =
    storeCompanies?.find((c) => c.id === activeCompanyId)?.role ?? selectedRole;
  const isInvestorFlow = activeCompanyRole === CompanyRole.INVESTOR;
  const investorGateOpen =
    vettingStatus === "onboarded" || vettingStatus === "auto_onboarded" || vettingEnabled === false;
  // Investors connect only once vetted; fundraisers connect as before.
  const canConnect = createdCompany !== null && (!isInvestorFlow || investorGateOpen);

  // Only start onboarding session after company is created (and, for
  // investors, after vetting approval — RD-1/RD-15).
  const {
    status,
    company,
    currentQuestion,
    progress,
    history,
    message,

    error,
    validationError,
    completion,
    isAwaitingNextStep,
    finalMessage,
    onboardingMeta,
    failedSend,
    submitAnswer,
    retryPendingAnswer,
    uploadAnswerFile,
    disconnect,
    reconnect,
  } = useOnboardingSession({
    autoConnect: canConnect,
    onCompletion: onComplete,
  });

  const THINKING_MSG_ID = "ai-thinking";
  const FINAL_MSG_ID = "ai-final-message";
  const SUMMARY_MSG_ID = "ai-summary-generating";

  const isLoading = status === "initializing" || status === "connecting";
  const isConnected = status === "connected";
  const isProcessingSummary = status === "processing_summary";
  const isCompleted = status === "completed" && Boolean(completion);
  const isError = status === "error";
  const isPreOnboarding = !createdCompany && !isCheckingExistingCompany;

  const [textValue, setTextValue] = useState("");
  const [choiceValue, setChoiceValue] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [pendingAnswerId, setPendingAnswerId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [awaitingTimedOut, setAwaitingTimedOut] = useState(false);
  const [awaitingHintVisible, setAwaitingHintVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  // Nonce-tagged text handed back to the composer when the user picks "Edit" on a failed send.
  const [restoreDraft, setRestoreDraft] = useState<{ text: string; nonce: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileTextareaRef = useRef<HTMLTextAreaElement>(null);
  const hasRedirectedRef = useRef(false);
  const completeFiredRef = useRef(false);

  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const getCompanies = useStore((state) => state.getCompanies);
  const invalidateCompanies = useStore((state) => state.invalidateCompanies);

  // Get company ID from route params or created company
  const companyId = routeCompanyId ?? createdCompany?.id ?? company?.id;

  // Reset redirect ref when company ID changes
  useEffect(() => {
    hasRedirectedRef.current = false;
  }, [companyId]);

  // Fetch the vetting status whenever the active company is an investor company.
  useEffect(() => {
    if (!isInvestorFlow || !createdCompany) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: statusError } = await investorVettingService.getVettingStatus();
      if (!cancelled) {
        if (statusError || !data) {
          console.info("[onboarding] Failed to fetch vetting status", { hasError: Boolean(statusError) });
          // 078 fail-safe: assume vetting is enabled — the gate stays closed.
          setVettingStatus("none");
          setVettingEnabled(true);
        } else {
          setVettingStatus(data.data.status);
          setVettingEnabled(data.data.vetting_enabled);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInvestorFlow, createdCompany]);

  const handleSynopsisSubmit = useCallback(async () => {
    const synopsis = synopsisValue.trim();
    if (synopsis.length < 20) {
      setSynopsisError("Please tell us a bit more — at least 20 characters.");
      return;
    }
    if (!createdCompany) {
      return;
    }
    setIsSubmittingSynopsis(true);
    setSynopsisError(null);
    const { data, error: submitError } = await investorVettingService.submitSynopsis({
      company_id: createdCompany.id,
      synopsis,
    });
    if (submitError) {
      setSynopsisError("We couldn't submit your introduction. Please try again.");
      setIsSubmittingSynopsis(false);
      return;
    }
    // 078: with vetting disabled the backend short-circuits to auto_onboarded,
    // which opens the gate immediately; otherwise the job is pending as before.
    setVettingStatus(data?.data.status ?? "pending");
    setIsSubmittingSynopsis(false);
  }, [synopsisValue, createdCompany]);

  // Check for existing incomplete company on mount
  useEffect(() => {
    const checkExistingCompany = async () => {
      setIsCheckingExistingCompany(true);

      // Get companies from store (will trigger fetch if needed)
      const currentCompanies = getCompanies();

      if (currentCompanies) {
        const incompleteCompany = currentCompanies.find((c) => !c.onboarding_completed);
        if (incompleteCompany) {
          setCreatedCompany({ id: incompleteCompany.id, name: incompleteCompany.name });
        }
        setIsCheckingExistingCompany(false);
        return;
      }

      // If no companies in store, fetch them
      await fetchCompanies({ force: true });
      const freshCompanies = getCompanies();

      if (freshCompanies) {
        const incompleteCompany = freshCompanies.find((c) => !c.onboarding_completed);
        if (incompleteCompany) {
          setCreatedCompany({ id: incompleteCompany.id, name: incompleteCompany.name });
        }
      }

      setIsCheckingExistingCompany(false);
    };

    void checkExistingCompany();
  }, [getCompanies, fetchCompanies]);

  // Add history items as messages when they're available
  useEffect(() => {
    if (history && history.length > 0) {
      const historyMessages: TMessage[] = [];

      history.forEach((item) => {
        // Add question if not already present
        const questionId = `question-${item.question.id}`;
        const questionExists = messages.some((msg) => msg.id === questionId);

        if (!questionExists) {
          const questionText = item.question.question_text;
          // Question text only - description shown separately
          historyMessages.push({
            id: questionId,
            text: questionText,
            sender: "ai",
            helperText: item.question.required === false ? "This question is optional" : undefined,
            // Answer guidance fields
            description: item.question.question_description ?? undefined,
            answerType: item.question.data_type,
            answerTypeLabel: getAnswerTypeLabel(item.question.data_type),
            answerGuidance: getAnswerGuidanceFromRequirements(
              item.question.answer_requirements,
              item.question.data_type
            ),
          });
        }

        // Add answer - replace optimistic message if it exists
        const answerId = `answer-${item.question.id}`;
        const answerExists = messages.some((msg) => msg.id === answerId);

        if (
          (!answerExists || messages.some((msg) => msg.id === answerId && msg.optimistic)) &&
          (item.answer.text || item.answer.fileId)
        ) {
          const answerText = item.answer.text ?? "";

          historyMessages.push({
            id: answerId,
            text: isSkipText(answerText) ? "Skipped" : answerText || (item.answer.fileId ? "" : "(No text answer)"),
            sender: "user",
            files: item.answer.fileId
              ? [
                  {
                    id: item.answer.fileId,
                    name: item.answer.fileName ?? "Uploaded file",
                  },
                ]
              : undefined,
          });
        }
      });

      if (historyMessages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          // Pluck the transient tail bubbles out so late history can never wedge itself
          // between the AI's closing message and the summary progress item.
          const tail = TAIL_IDS.map((id) => prev.find((m) => m.id === id)).filter((m): m is TMessage => Boolean(m));
          const filtered = prev.filter((msg) => {
            if (TAIL_IDS.includes(msg.id)) return false;
            const willBeReplaced = historyMessages.some((newMsg) => newMsg.id === msg.id && msg.optimistic);
            return !willBeReplaced;
          });
          const newMessages = historyMessages.filter(
            (m) => !existingIds.has(m.id) || prev.some((existing) => existing.id === m.id && existing.optimistic)
          );
          return [...filtered, ...newMessages, ...tail];
        });
      }
    }
  }, [history, messages]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    isAtBottomRef.current = atBottom;
    setShowJumpToBottom(!atBottom);
  }, []);

  const scrollToLatest = useCallback((behavior: ScrollBehavior = "smooth") => {
    isAtBottomRef.current = true;
    setShowJumpToBottom(false);
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Auto-scroll to bottom when messages change — only if already at bottom
  useEffect(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Ensure validation or local errors are visible without manual scrolling
  useEffect(() => {
    if (localError || validationError) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [localError, validationError]);

  // Remove optimistic messages when a validation error is returned from the server
  useEffect(() => {
    if (!validationError || !pendingAnswerId) {
      return;
    }

    setMessages((prev) => prev.filter((msg) => !(msg.id === pendingAnswerId && msg.optimistic)));
    setPendingAnswerId(null);
  }, [validationError, pendingAnswerId]);

  useEffect(() => {
    setTextValue("");
    setChoiceValue("");
    setLocalError(null);
    setPendingAnswerId(null);
  }, [currentQuestion?.id]);

  // Auto-resize textarea function
  const autoResizeTextarea = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return;
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set height based on scrollHeight, with min and max constraints
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 80), 600);
    textarea.style.height = `${newHeight}px`;
  }, []);

  // Auto-resize textarea when textValue changes
  useEffect(() => {
    autoResizeTextarea(textareaRef.current);
  }, [textValue, autoResizeTextarea]);

  // Auto-resize file textarea when textValue changes (for file upload questions)
  useEffect(() => {
    autoResizeTextarea(fileTextareaRef.current);
  }, [textValue, autoResizeTextarea]);

  // Reset textarea height when question changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    if (fileTextareaRef.current) {
      fileTextareaRef.current.style.height = "auto";
    }
  }, [currentQuestion?.id]);

  // Show a subtle hint after 5s, then escalate to a reconnect prompt after 20s
  useEffect(() => {
    if (!isAwaitingNextStep) {
      setAwaitingHintVisible(false);
      setAwaitingTimedOut(false);
      return;
    }
    const hintTimer = setTimeout(() => setAwaitingHintVisible(true), 5_000);
    const reconnectTimer = setTimeout(() => setAwaitingTimedOut(true), 20_000);
    return () => {
      clearTimeout(hintTimer);
      clearTimeout(reconnectTimer);
    };
  }, [isAwaitingNextStep]);

  // Closing message + summary progress, as two separate bubbles.
  //
  // The AI's last turn is a real message and must render as one; the "generating your
  // summary" indicator sits directly below it as a transient progress item. Previously
  // both were crammed into a single bubble with `thinking: true`, which made the
  // renderer drop the text entirely — the user never learned why the chat had ended.
  //
  // Triggers on isProcessingSummary directly so the bubbles appear in the same render
  // cycle as the WebSocket "processing" frame — no one-cycle gap where the chat is blank
  // and older messages (e.g. "What is your company name?") are exposed.
  useEffect(() => {
    if (!summaryGenerating && !isProcessingSummary) return;

    setMessages((prev) => {
      const withoutTail = prev.filter((m) => m.id !== FINAL_MSG_ID && m.id !== SUMMARY_MSG_ID);
      const next: TMessage[] = [...withoutTail];

      const closingText = finalMessage?.text;
      if (closingText) {
        next.push({ id: FINAL_MSG_ID, text: closingText, sender: "ai", kind: "message" });
      }

      next.push({
        id: SUMMARY_MSG_ID,
        text: "",
        sender: "ai",
        kind: "progress",
        thinking: true,
        progressStage: summaryProgress?.stage ?? null,
        progressElapsedSeconds: summaryProgress?.elapsedSeconds ?? null,
      });

      return next;
    });
  }, [
    summaryGenerating,
    isProcessingSummary,
    SUMMARY_MSG_ID,
    FINAL_MSG_ID,
    finalMessage?.text,
    summaryProgress?.stage,
    summaryProgress?.elapsedSeconds,
  ]);

  // Remove the AI "thinking" bubble once the next question arrives.
  // Adding it is done synchronously inside handleSubmit to guarantee
  // it always appears below the user's answer.
  useEffect(() => {
    if (!isAwaitingNextStep) {
      setMessages((prev) => prev.filter((m) => m.id !== THINKING_MSG_ID));
    }
  }, [isAwaitingNextStep, THINKING_MSG_ID]);

  // While the socket is open but the first guided message hasn't arrived yet, show a
  // thinking bubble instead of the empty "No messages yet." state so the user sees the
  // AI is preparing their first question.
  const displayMessages = useMemo<TMessage[]>(() => {
    if (isConnected && messages.length === 0 && !isCompleted && !isProcessingSummary && !isError) {
      return [{ id: "ai-initial-thinking", text: "", sender: "ai", thinking: true }];
    }
    return messages;
  }, [isConnected, messages, isCompleted, isProcessingSummary, isError]);

  // Add pre-onboarding role selection question
  useEffect(() => {
    if (isPreOnboarding && preOnboardingStep === "role_selection") {
      const questionExists = messages.some((msg) => msg.id === "pre-role-selection");
      if (!questionExists) {
        setMessages([
          {
            id: "pre-role-selection",
            text: "👋 Welcome to Fundy!\n\nWe're excited to have you here. Answer one quick question so we can tailor your onboarding and recommendations to your goals.\n\nAre you joining as a founder or an investor?",
            sender: "ai",
          },
        ]);
      }
    }
  }, [isPreOnboarding, preOnboardingStep, messages]);

  // Add role selection answer as user message
  useEffect(() => {
    if (selectedRole && preOnboardingStep === "company_name") {
      const answerExists = messages.some((msg) => msg.id === "pre-role-answer");
      if (!answerExists) {
        setMessages((prev) => [
          ...prev,
          {
            id: "pre-role-answer",
            text: selectedRole === CompanyRole.FUNDRAISER ? "Founder" : "Investor",
            sender: "user",
          },
        ]);
      }
    }
  }, [selectedRole, preOnboardingStep, messages]);

  // Add company name question
  useEffect(() => {
    if (isPreOnboarding && preOnboardingStep === "company_name") {
      const questionExists = messages.some((msg) => msg.id === "pre-company-name");
      if (!questionExists) {
        setMessages((prev) => [
          ...prev,
          {
            id: "pre-company-name",
            text: "What is your company name?\n\nPlease enter your company name to continue.",
            sender: "ai",
          },
        ]);
      }
    }
  }, [isPreOnboarding, preOnboardingStep, messages]);

  // Add company name answer as user message
  useEffect(() => {
    if (createdCompany) {
      const answerExists = messages.some((msg) => msg.id === "pre-company-answer");
      if (!answerExists && companyName.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            id: "pre-company-answer",
            text: companyName.trim(),
            sender: "user",
          },
        ]);
      }
    }
  }, [createdCompany, companyName, messages]);

  // Add question as AI message when it arrives
  useEffect(() => {
    if (currentQuestion && isConnected) {
      const questionText = currentQuestion.question_text;
      // Question text only - description shown separately in the UI

      // Check if this question is already in messages
      const questionExists = messages.some((msg) => msg.id === `question-${currentQuestion.id}` && msg.sender === "ai");

      if (!questionExists) {
        const questionMessage: TMessage = {
          id: `question-${currentQuestion.id}`,
          text: questionText,
          sender: "ai",
          helperText: currentQuestion.required === false ? "This question is optional" : undefined,
          // Answer guidance fields
          description: currentQuestion.question_description ?? undefined,
          answerType: currentQuestion.data_type,
          answerTypeLabel: getAnswerTypeLabel(currentQuestion.data_type),
          answerGuidance: getAnswerGuidanceFromRequirements(
            currentQuestion.answer_requirements,
            currentQuestion.data_type
          ),
        };
        setMessages((prev) => [...prev, questionMessage]);
      }
    }
  }, [currentQuestion, isConnected, messages]);

  const answeredCount = progress?.answered_questions ?? 0;
  const totalQuestions = progress?.total_questions ?? (currentQuestion ? answeredCount + 1 : answeredCount);
  const completionPercentage = progress?.percentage ?? progress?.completion_percentage ?? 0;
  const isLastQuestion = progress?.total_questions != null && answeredCount === progress.total_questions - 1;

  // Repeat-question prefill: if the AI re-asks something near-identical, offer
  // the earlier answer as a one-tap prefill (never auto-sent).
  const repeatPrefill = useMemo(() => {
    if (!currentQuestion) return null;
    return findSimilarPriorAnswer(currentQuestion.question_text, history);
  }, [currentQuestion, history]);

  // Live progress projected from the on-the-wire onboarding meta — real numbers,
  // degrades silently when the backend doesn't publish them.
  const sessionProgress = useMemo(() => projectSessionProgress(onboardingMeta, progress), [onboardingMeta, progress]);

  // Hand off to the page once, as soon as processing starts; it owns summary polling and
  // navigation. `history`/`progress` are in the dep array, so without this guard the
  // handoff re-fired on every state change — duplicate POST /complete-onboarding calls
  // and a leaked polling interval per fire.
  useEffect(() => {
    if (isProcessingSummary && company?.id && !completeFiredRef.current) {
      completeFiredRef.current = true;
      onComplete({
        companyId: company.id,
        history,
        progress: progress ?? {
          total_questions: totalQuestions,
          answered_questions: answeredCount,
          completion_percentage: completionPercentage,
        },
        message: "Processing your onboarding summary...",
        nextSteps: [],
      });
    }
  }, [
    isProcessingSummary,
    company?.id,
    history,
    progress,
    totalQuestions,
    answeredCount,
    completionPercentage,
    onComplete,
  ]);

  const metadata = currentQuestion?.metadata as TOnboardingQuestionMetadata | undefined;

  const textPlaceholder =
    typeof metadata?.text_placeholder === "string"
      ? metadata.text_placeholder
      : typeof metadata?.placeholder === "string"
        ? metadata.placeholder
        : undefined;

  const validateAnswer = useCallback(
    (options?: { files?: TMessageFile[]; text?: string }): string | null => {
      if (!currentQuestion) {
        return "No active question.";
      }

      const isRequired = currentQuestion.required !== false;
      const trimmedText =
        currentQuestion.data_type === "multiple_choice" ? choiceValue.trim() : (options?.text ?? textValue).trim();
      const hasText = trimmedText.length > 0;
      const hasFile = Boolean(options?.files && options.files.length > 0);

      switch (currentQuestion.data_type) {
        case "text":
        case "multiple_choice": {
          if (isRequired && !hasText) {
            return "Please provide a response before submitting.";
          }
          return null;
        }
        case "number": {
          if (!hasText) {
            return isRequired ? "Please enter a number." : null;
          }
          const value = Number(trimmedText.replace(/,/g, ""));
          if (!Number.isFinite(value)) {
            return "Please enter a valid number.";
          }
          return null;
        }
        case "file_upload": {
          if (isRequired && !hasFile) {
            return "Please upload a file before continuing.";
          }
          return null;
        }
        case "text_or_file": {
          if (isRequired && !hasText && !hasFile) {
            return "Please provide a text response or upload a file.";
          }
          return null;
        }
        case "text_with_file": {
          if (!hasText) {
            return "Text is required for this question.";
          }
          return null;
        }
        case "file_with_text": {
          if (isRequired && !hasFile) {
            return "File upload is required for this question.";
          }
          return null;
        }
        default:
          return null;
      }
    },
    [choiceValue, currentQuestion, textValue]
  );

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement> | string, files?: TMessageFile[]) => {
      if (typeof event === "string") {
        // Called from OnboardingMessageInput's onSend
        setTextValue(event);
      } else {
        event?.preventDefault();
      }

      if (!currentQuestion || !company) {
        setLocalError("Onboarding question unavailable. Please try again.");
        return;
      }

      // Handle files from OnboardingMessageInput
      // OnboardingMessageInput will have already uploaded the files through the onboarding service
      // So we just need to extract the file IDs from the MessageFile array
      let fileId: string | null = null;
      let fileName: string | null = null;

      if (files && files.length > 0) {
        // OnboardingMessageInput provides MessageFile with the uploaded file ID
        const uploadedFile = files[0]; // Take the first file
        fileId = uploadedFile.id;
        fileName = uploadedFile.name;
      }

      // Get text value - from OnboardingMessageInput callback or state.
      // An explicit string argument (skip utterance, etc.) always wins.
      const messageText = typeof event === "string" ? event : textValue;
      const rawTrimmed =
        currentQuestion.data_type === "multiple_choice" && typeof event !== "string"
          ? choiceValue.trim()
          : messageText.trim();
      // Strip thousand-separator commas from number answers before sending to the backend
      const trimmedText = currentQuestion.data_type === "number" ? rawTrimmed.replace(/,/g, "") : rawTrimmed;
      const textPayload = currentQuestion.data_type === "file_upload" && trimmedText.length === 0 ? null : trimmedText;

      // Validate - pass files and text from OnboardingMessageInput to validation
      const validationMessage = validateAnswer({ files, text: messageText });
      if (validationMessage) {
        setLocalError(validationMessage);
        return;
      }

      setLocalError(null);

      // Add user answer as message immediately (optimistic update)
      // Use consistent ID format so history can replace it later
      const answerId = `answer-${currentQuestion.id}`;
      const answerText = trimmedText ?? "";

      const answerMessage: TMessage = {
        id: answerId,
        text: isSkipText(answerText)
          ? "Skipped"
          : answerText || (fileId ? "" : currentQuestion.required === false ? "Skipped" : "(No text answer)"),
        sender: "user",
        optimistic: true,
        files: fileId
          ? [
              {
                id: fileId,
                name: fileName ?? "Uploaded file",
              },
            ]
          : undefined,
      };

      // Add the answer then immediately append the thinking bubble in one update
      // so the bubble always appears below the answer, never above it.
      setMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== answerId && msg.id !== THINKING_MSG_ID);
        return [...filtered, answerMessage, { id: THINKING_MSG_ID, text: "", sender: "ai", thinking: true }];
      });
      setPendingAnswerId(answerId);
      // Sending your own message always returns you to the latest position,
      // even if you had scrolled up to reread earlier turns.
      scrollToLatest("smooth");

      submitAnswer({
        question: currentQuestion,
        text: textPayload,
        fileId,
        fileName,
      });
    },
    [choiceValue, company, currentQuestion, scrollToLatest, submitAnswer, textValue, validateAnswer]
  );

  // Pre-onboarding handlers
  const handleRoleSelection = useCallback((role: CompanyRole) => {
    setSelectedRole(role);
    setPreOnboardingError(null);
    setPreOnboardingStep("company_name");
    // Add role answer as user message
    setMessages((prev) => {
      const answerExists = prev.some((msg) => msg.id === "pre-role-answer");
      if (!answerExists) {
        return [
          ...prev,
          {
            id: "pre-role-answer",
            text: role === CompanyRole.FUNDRAISER ? "Founder" : "Investor",
            sender: "user",
          },
        ];
      }
      return prev;
    });
  }, []);

  const handleCompanyNameSubmit = useCallback(async () => {
    if (!selectedRole) {
      setPreOnboardingError("Please select a role.");
      return;
    }

    const trimmedCompanyName = companyName.trim();
    if (trimmedCompanyName.length === 0) {
      setPreOnboardingError("Please enter a company name.");
      return;
    }

    setPreOnboardingStep("creating_company");
    setPreOnboardingError(null);

    try {
      const { data, error } = await companiesService.createCompany({
        role: selectedRole,
        name: trimmedCompanyName,
      });

      if (error || !data?.data) {
        const errorMessage = error
          ? await getCreateCompanyErrorMessage(error)
          : (data?.message ?? "Failed to create company. Please try again.");
        setPreOnboardingError(errorMessage);
        setPreOnboardingStep("company_name");
        return;
      }

      // Add company name as user message
      setMessages((prev) => {
        const answerExists = prev.some((msg) => msg.id === "pre-company-answer");
        if (!answerExists) {
          return [
            ...prev,
            {
              id: "pre-company-answer",
              text: trimmedCompanyName,
              sender: "user",
            },
          ];
        }
        return prev;
      });

      // Company created successfully
      const newCompany = {
        id: data.data.id,
        name: data.data.name,
      };

      // Invalidate companies cache and refresh so route guards can find the new company
      invalidateCompanies();
      await fetchCompanies({ force: true });

      // Verify the company is in the store before navigating
      const refreshedCompanies = getCompanies();
      const verifiedCompany = refreshedCompanies?.find((c) => c.id === newCompany.id);

      if (!verifiedCompany) {
        // If company not found, try one more fetch and wait a bit
        await fetchCompanies({ force: true });
        // Small delay to ensure store is updated
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setCreatedCompany(newCompany);

      // Navigate to company-specific onboarding route immediately after creation
      // This ensures the user is always on the correct route for the new company
      const currentPath = location.pathname;
      const targetPath = `/app/companies/${newCompany.id}/onboarding`;

      // Only navigate if we're not already on the correct route
      if (currentPath !== targetPath) {
        setHasNavigated(true);
        void navigate({
          to: "/app/companies/$company_id/onboarding",
          params: { company_id: newCompany.id },
          replace: true,
        });
      }
    } catch {
      setPreOnboardingError("An unexpected error occurred. Please try again.");
      setPreOnboardingStep("company_name");
    }
  }, [selectedRole, companyName, invalidateCompanies, fetchCompanies, getCompanies, location.pathname, navigate]);

  // Navigate to company-specific onboarding route after company is created
  // Only if we're on the general /app/onboarding route (not company-specific)
  // This is a fallback in case navigation didn't happen in handleCompanyNameSubmit
  useEffect(() => {
    if (createdCompany && !hasNavigated) {
      const isGeneralOnboardingRoute = location.pathname === "/app/onboarding";

      if (isGeneralOnboardingRoute) {
        setHasNavigated(true);
        void navigate({
          to: "/app/companies/$company_id/onboarding",
          params: { company_id: createdCompany.id },
          replace: true,
        });
      }
    }
  }, [createdCompany, hasNavigated, location.pathname, navigate]);

  // Trigger reconnect when company is created (backup in case autoConnect doesn't trigger)
  useEffect(() => {
    if (canConnect && reconnect && status === "idle") {
      void reconnect();
    }
  }, [canConnect, reconnect, status]);

  const handleCancel = useCallback(() => {
    if (answeredCount > 0) {
      setShowCancelDialog(true);
      return;
    }
    if (createdCompany) {
      disconnect();
    }
    onCancel();
  }, [answeredCount, createdCompany, disconnect, onCancel]);

  const handleConfirmCancel = useCallback(() => {
    setShowCancelDialog(false);
    if (createdCompany) {
      disconnect();
    }
    onCancel();
  }, [createdCompany, disconnect, onCancel]);

  // Redirect a company whose onboarding is already finished straight to its summary.
  //
  // This deliberately does NOT handle the just-completed case: OnboardingPage owns that
  // navigation so the closing message and progress bubble stay on screen until the summary
  // is actually ready. Racing it from here used to cut the chat short after 5s — or sooner,
  // whenever the page's own poll or error path fired first.
  useEffect(() => {
    if (hasRedirectedRef.current || !companyId) {
      return;
    }

    // Check for "Already completed" variations in error or message
    const checkAlreadyCompleted = (text: string | undefined): boolean => {
      if (!text) return false;
      const lower = text.toLowerCase();
      return (
        lower.includes("already completed") ||
        lower.includes("onboarding already completed") ||
        lower.includes("already completed for this company")
      );
    };

    if (checkAlreadyCompleted(error?.message) || checkAlreadyCompleted(message)) {
      hasRedirectedRef.current = true;
      void (async () => {
        invalidateCompanies();
        await fetchCompanies({ force: true });
        void navigate({
          to: "/app/companies/$company_id/onboarding/summary",
          params: { company_id: companyId },
          replace: true,
        });
      })();
    }
  }, [error, message, companyId, navigate, invalidateCompanies, fetchCompanies]);

  // Keep the chat mounted through processing_summary so the closing bubble is visible.
  const showChat = (isConnected || isProcessingSummary) && !isCompleted && !isPreOnboarding;
  const showPreOnboardingChat = isPreOnboarding;

  return (
    <div className="flex h-full flex-col">
      {/* Unified header — shown in all states */}
      <header className="flex flex-shrink-0 items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary">
            <MessageCircle className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Company Onboarding</p>
            {isConnected && currentQuestion ? (
              <p className="text-xs text-muted-foreground">
                Question {answeredCount + 1}
                {progress?.total_questions != null ? ` of ${progress.total_questions}` : ""}
                {sessionProgress?.areasCovered != null && (
                  <span className="ml-1.5">· {sessionProgress.areasCovered} areas covered</span>
                )}
                {isLastQuestion && <span className="ml-1.5 font-medium text-primary">· Final question</span>}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Share your company information through conversation</p>
            )}
            {/* Live coverage bar — real numbers from the session meta, hidden
                until the backend publishes a percent. */}
            {isConnected && sessionProgress?.percent != null && (
              <div className="mt-1 h-1 w-40 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-1 rounded-full bg-primary transition-all duration-500 ease-out"
                  style={{ width: `${sessionProgress.percent}%` }}
                  role="progressbar"
                  aria-valuenow={sessionProgress.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <FeedbackWidget
            context="onboarding"
            variant="icon"
          />
          {status === "connected" && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-500">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Connected
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={showChat ? handleCancel : onCancel}
          >
            Cancel
          </Button>
        </div>
      </header>

      {/* Pre-onboarding chat */}
      {showPreOnboardingChat && (
        <div className="relative flex flex-1 flex-col overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            onScroll={handleScroll}
          >
            <OnboardingMessageList messages={messages} />
            {preOnboardingStep === "creating_company" && (
              <div className="px-6 pb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Creating your company...</span>
                </div>
              </div>
            )}
            {preOnboardingError && preOnboardingStep !== "company_name" && (
              <div className="px-6 pb-4">
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {preOnboardingError}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showJumpToBottom && (
            <button
              onClick={() => scrollToLatest("smooth")}
              className="absolute bottom-20 right-4 z-10 flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium shadow-md hover:bg-muted"
            >
              <ArrowDown className="h-3.5 w-3.5" />
              Jump to latest
            </button>
          )}
          {/* Chat Input Area for pre-onboarding */}
          <div>
            {preOnboardingStep === "role_selection" && (
              <div className="px-4 py-3">
                <div className="flex gap-3 rounded-2xl border border-border bg-card px-3 py-3">
                  <Button
                    type="button"
                    variant={selectedRole === CompanyRole.FUNDRAISER ? "default" : "outline"}
                    onClick={() => handleRoleSelection(CompanyRole.FUNDRAISER)}
                    className="flex-1 rounded-xl"
                  >
                    Founder
                  </Button>
                  <Button
                    type="button"
                    variant={selectedRole === CompanyRole.INVESTOR ? "default" : "outline"}
                    onClick={() => handleRoleSelection(CompanyRole.INVESTOR)}
                    className="flex-1 rounded-xl"
                  >
                    Investor
                  </Button>
                </div>
              </div>
            )}

            {preOnboardingStep === "company_name" && (
              <form
                className="px-4 py-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleCompanyNameSubmit();
                }}
              >
                <div className="flex items-end gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-sm">
                  <Input
                    value={companyName}
                    onChange={(event) => {
                      setCompanyName(event.target.value);
                      setPreOnboardingError(null);
                    }}
                    placeholder="Enter company name"
                    autoFocus
                    aria-invalid={preOnboardingError ? true : undefined}
                    aria-describedby={preOnboardingError ? "company-name-error" : undefined}
                    className={`flex-1 border-0 shadow-none focus-visible:ring-0 ${preOnboardingError ? "text-destructive" : ""}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && companyName.trim().length > 0) {
                        e.preventDefault();
                        void handleCompanyNameSubmit();
                      }
                    }}
                  />
                  <button
                    type="submit"
                    aria-label="Submit company name"
                    disabled={
                      companyName.trim().length === 0 ||
                      (preOnboardingStep as TPreOnboardingStep) === "creating_company"
                    }
                    className="mb-0.5 flex flex-shrink-0 items-center justify-center rounded-xl bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {(preOnboardingStep as TPreOnboardingStep) === "creating_company" ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <ArrowRight className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {preOnboardingError && (
                  <p
                    id="company-name-error"
                    role="alert"
                    className="mt-2 text-sm text-destructive"
                  >
                    {preOnboardingError}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      {!showChat && message && (
        <div className="mx-8 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary">
          {message}
        </div>
      )}

      {!showChat && isError && (
        <div className="mx-8 flex flex-col items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-5 text-center">
          <p className="text-sm text-destructive">
            {typeof error === "string"
              ? error
              : (error?.message ?? "Check your connection. Your progress has been saved.")}
          </p>
          {reconnect && (
            <button
              type="button"
              onClick={() => void reconnect()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <RefreshCw className="h-4 w-4" />
              Reconnect
            </button>
          )}
        </div>
      )}

      {isCheckingExistingCompany && (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span>Checking for existing onboarding…</span>
          </div>
        </div>
      )}

      {/* Investor vetting gate (006): synopsis step + pending/declined states */}
      {createdCompany && isInvestorFlow && !investorGateOpen && !isCheckingExistingCompany && (
        <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
          {vettingStatus === "unknown" && (
            <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span>Checking your onboarding status…</span>
            </div>
          )}

          {vettingStatus === "none" && (
            <div className="w-full max-w-xl space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Tell us about yourself</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Share a short introduction — who you are, what kind of investor you are, and what you're looking to
                  invest in. A member of our team will review it and be in touch.
                </p>
              </div>
              <Textarea
                value={synopsisValue}
                onChange={(event) => {
                  setSynopsisValue(event.target.value);
                  setSynopsisError(null);
                }}
                rows={5}
                maxLength={2000}
                placeholder="e.g. Partner at XYZ Capital — we invest in seed-stage agritech across East Africa…"
                data-testid="vetting-synopsis-input"
              />
              {synopsisError && (
                <p
                  role="alert"
                  className="text-sm text-destructive"
                >
                  {synopsisError}
                </p>
              )}
              <Button
                type="button"
                onClick={() => void handleSynopsisSubmit()}
                disabled={isSubmittingSynopsis || synopsisValue.trim().length === 0}
                className="w-full"
              >
                {isSubmittingSynopsis ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Submit introduction
              </Button>
            </div>
          )}

          {(vettingStatus === "pending" || vettingStatus === "claimed") && (
            <div className="w-full max-w-xl rounded-lg border border-primary/40 bg-primary/10 px-5 py-6 text-center">
              <h2 className="text-lg font-semibold text-foreground">Thanks — we've got your details</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                A member of our team will be in touch with you shortly to complete your investor onboarding. Once you've
                been onboarded you'll receive an email with a link to continue your registration.
              </p>
            </div>
          )}

          {vettingStatus === "declined" && (
            <div className="w-full max-w-xl rounded-lg border border-destructive/40 bg-destructive/10 px-5 py-6 text-center">
              <h2 className="text-lg font-semibold text-foreground">Investor onboarding unavailable</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                We're unable to onboard you as an investor at this time. If you believe this is a mistake, please
                contact our support team.
              </p>
            </div>
          )}
        </div>
      )}

      {(isLoading || (status === "idle" && !isPreOnboarding && canConnect)) && !isCheckingExistingCompany && (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span>Preparing onboarding questions…</span>
          </div>
        </div>
      )}

      {showChat && (
        <div className="relative flex flex-1 flex-col overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            onScroll={handleScroll}
            data-tour="onboarding-messages"
          >
            <OnboardingMessageList messages={displayMessages} />
            {awaitingHintVisible && isAwaitingNextStep && !awaitingTimedOut && (
              <div className="px-6 pb-2">
                <p className="text-center text-xs text-muted-foreground">Almost there, just a moment…</p>
              </div>
            )}
            {awaitingTimedOut && isAwaitingNextStep && reconnect && (
              <div className="px-6 pb-4">
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2">
                  <span className="text-xs text-muted-foreground">
                    Taking longer than expected. Your progress is saved.
                  </span>
                  <button
                    type="button"
                    onClick={() => void reconnect()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Reconnect
                  </button>
                </div>
              </div>
            )}
            {(localError != null || validationError != null) && (
              <div className="px-6 pb-4">
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {validationError?.message ?? localError}
                </div>
              </div>
            )}
            {/* Failed send — the answer is kept server-side; offer Resend without retyping. */}
            {failedSend && !isAwaitingNextStep && (
              <div className="px-6 pb-4">
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                  <span className="text-xs text-muted-foreground">
                    Your answer couldn't be processed
                    {failedSend.fileName ? ` (${failedSend.fileName} was attached)` : ""}. Nothing was lost.
                  </span>
                  <button
                    type="button"
                    onClick={retryPendingAnswer}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Resend
                  </button>
                  {failedSend.text && (
                    <button
                      type="button"
                      onClick={() => setRestoreDraft({ text: failedSend.text ?? "", nonce: Date.now() })}
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    >
                      Edit instead
                    </button>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showJumpToBottom && (
            <button
              onClick={() => scrollToLatest("smooth")}
              className="absolute bottom-20 right-4 z-10 flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium shadow-md hover:bg-muted"
            >
              <ArrowDown className="h-3.5 w-3.5" />
              Jump to latest
            </button>
          )}
          {/* Chat Input Area */}
          {currentQuestion && (
            <div data-tour="onboarding-input">
              <>
                {/* Special inputs for multiple choice */}
                {currentQuestion.data_type === "multiple_choice" && (
                  <div className="px-4 py-3">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit(e);
                      }}
                    >
                      <div className="flex items-center gap-2 rounded-2xl border border-border bg-card px-3 py-2 shadow-sm">
                        {/* Universal skip — ghost affordance on choice questions too. */}
                        {!choiceValue.trim() && !isAwaitingNextStep && (
                          <button
                            type="button"
                            onClick={() => void handleSubmit(SKIP_UTTERANCE)}
                            title="Skip this question — you can fill it in later; skipping may leave gaps in your profile."
                            className="flex-shrink-0 px-2 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
                          >
                            Skip
                          </button>
                        )}
                        <select
                          className="flex-1 bg-transparent py-2 text-sm text-foreground focus:outline-none"
                          value={choiceValue}
                          onChange={(event) => setChoiceValue(event.target.value)}
                          disabled={isAwaitingNextStep}
                        >
                          <option
                            value=""
                            disabled
                          >
                            Select an option
                          </option>
                          {currentQuestion?.choices?.map((option) => (
                            <option
                              key={option}
                              value={option}
                            >
                              {option}
                            </option>
                          ))}
                        </select>
                        <button
                          type="submit"
                          disabled={isAwaitingNextStep || choiceValue.trim().length === 0}
                          className="flex flex-shrink-0 items-center justify-center rounded-xl bg-primary p-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Send"
                        >
                          {isAwaitingNextStep ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <ArrowRight className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Use OnboardingMessageInput for text, number, and file questions */}
                {(currentQuestion.data_type === "text" ||
                  currentQuestion.data_type === "number" ||
                  currentQuestion.data_type === "file_upload" ||
                  currentQuestion.data_type === "text_or_file" ||
                  currentQuestion.data_type === "text_with_file" ||
                  currentQuestion.data_type === "file_with_text") &&
                  company && (
                    <OnboardingMessageInput
                      onSend={(message, files) => {
                        setTextValue(message);
                        void handleSubmit(message, files);
                      }}
                      awaiting={isAwaitingNextStep}
                      prefill={repeatPrefill}
                      restoreText={restoreDraft}
                      uploadFile={uploadAnswerFile}
                      context={{
                        companyId: company.id,
                        questionId: currentQuestion.id,
                      }}
                      placeholder={
                        currentQuestion.data_type === "number"
                          ? (textPlaceholder ?? "Enter the amount in numbers...")
                          : (textPlaceholder ?? "Type your answer...")
                      }
                      acceptedFileTypes={
                        currentQuestion.answer_requirements?.file_types ??
                        currentQuestion.metadata?.file_types ??
                        undefined
                      }
                      answerRequirements={currentQuestion.answer_requirements ?? null}
                      dataType={currentQuestion.data_type}
                      required={currentQuestion.required}
                      sendLabel={isLastQuestion ? "Finish" : undefined}
                    />
                  )}
              </>
            </div>
          )}
        </div>
      )}

      {isCompleted && completion && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <div className="rounded-full bg-primary/10 p-4 text-primary">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <div className="max-w-md space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Onboarding complete</h2>
            <p className="text-sm text-muted-foreground">{completion.message}</p>
            {completion.aiSummary && (
              <div className="mt-4 rounded-md bg-primary/5 p-4 text-left">
                <h3 className="mb-2 text-sm font-semibold text-foreground">AI Summary</h3>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{completion.aiSummary}</p>
              </div>
            )}
            {completion.nextSteps.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {completion.nextSteps.map((step) => (
                  <li
                    key={step}
                    className="rounded-md bg-background px-3 py-2 text-left shadow-sm"
                  >
                    {step}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Cancel confirmation dialog — only shown when user has made progress */}
      <Dialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Leave onboarding?</DialogTitle>
            <DialogDescription>
              Your progress is saved. You can pick up exactly where you left off at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowCancelDialog(false)}
            >
              Continue onboarding
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
            >
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export type { TOnboardingCompletionResult };
